import { Link } from 'react-router-dom'

const steps = [
  {
    number: '01',
    title: 'Upload Your Product',
    description: 'Start with a snapshot of your product. No lightbox needed — a phone photo with decent lighting works great.',
    icon: 'add_a_photo',
    tip: 'Take a picture of your product on a simple, plain background for best results.',
  },
  {
    number: '02',
    title: 'Choose a Background Style Reference',
    description: 'Browse our curated style library and select a scene that matches your brand. Kalypso will create new professional photography based on your selection.',
    icon: 'palette',
    tip: 'This is what your background will look like. Clean simple backgrounds work best but background references with props also work well!',
  },
  {
    number: '03',
    title: 'Create & Download',
    description: 'Click Generate. In seconds, Kalypso creates brand-new, studio-quality listing images — no edits, just new photos created for you.',
    icon: 'shutter_speed',
    tip: 'Use 5-Shot mode to get multiple variations for A/B testing your listings.',
  },
]

const modes = [
  {
    name: '1-Shot Mode',
    description: 'Create a single listing image. Fast and efficient.',
    color: 'bg-blue-600',
    icon: 'filter_1',
    credits: '1 credit',
  },
  {
    name: '5-Shot Mode',
    description: 'Create 5 variations at once. Great for testing which image converts best.',
    color: 'bg-amber-600',
    icon: 'filter_5',
    credits: '5 credits',
  },
  {
    name: 'Autopilot',
    description: 'Always active. Kalypso analyzes your style reference to optimize settings automatically.',
    color: 'bg-primary',
    icon: 'auto_awesome',
    credits: 'Always on',
  },
  {
    name: 'White Background',
    description: 'Create clean product shots on pure white — marketplace-ready.',
    color: 'bg-gray-800',
    icon: 'crop_free',
    credits: 'Same cost',
  },
]

export function QuickStartGuide() {
  return (
    <>
      {/* Header */}
      <div className="animate-fade-up stagger-1">
        <div className="flex items-center gap-2 text-primary-dark font-bold text-xs tracking-widest uppercase mb-3">
          <span className="material-symbols-outlined text-lg">rocket_launch</span>
          Product Photography Software
        </div>
        <h1 className="text-3xl md:text-4xl font-serif font-semibold text-text-main leading-tight mb-3">
          Quick Start Guide
        </h1>
        <p className="text-text-muted text-base max-w-2xl">
          From snapshot to studio-quality listing in under a minute. No lightbox. No staging. No editing.
        </p>
      </div>

      {/* Key differentiator */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-5 border border-primary/20 animate-fade-up stagger-2">
        <p className="text-text-main font-medium text-center">
          Kalypso doesn't edit your photos. It <span className="text-primary-dark font-semibold">creates new professional product photography</span>.
        </p>
      </div>

      {/* Steps */}
      <div className="grid gap-6 animate-fade-up stagger-3">
        {steps.map((step) => (
          <div
            key={step.number}
            className="bg-white rounded-2xl border border-border-color p-6 md:p-8 shadow-card card-hover-premium"
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Step Number & Icon */}
              <div className="flex md:flex-col items-center md:items-start gap-4 md:gap-3">
                <div className="bg-primary/10 rounded-2xl p-4 flex items-center justify-center">
                  <span className="material-symbols-outlined text-3xl text-primary">{step.icon}</span>
                </div>
                <span className="text-4xl font-serif font-bold text-primary/20">{step.number}</span>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-xl font-serif font-semibold text-text-main mb-2">
                  {step.title}
                </h3>
                <p className="text-text-muted leading-relaxed mb-4">
                  {step.description}
                </p>
                <div className="flex items-start gap-2 bg-accent/30 rounded-lg p-3">
                  <span className="material-symbols-outlined text-sm text-primary-dark mt-0.5">lightbulb</span>
                  <p className="text-sm text-primary-dark">
                    <span className="font-semibold">Pro tip:</span> {step.tip}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Generation Modes */}
      <div className="animate-fade-up stagger-4">
        <h2 className="text-2xl font-serif font-medium text-text-main mb-6">
          Generation Modes
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {modes.map((mode) => (
            <div
              key={mode.name}
              className="bg-white rounded-xl border border-border-color p-5 shadow-card hover:shadow-soft transition-shadow"
            >
              <div className={`${mode.color} w-10 h-10 rounded-lg flex items-center justify-center mb-4`}>
                <span className="material-symbols-outlined text-white">{mode.icon}</span>
              </div>
              <h4 className="font-semibold text-text-main mb-1">{mode.name}</h4>
              <p className="text-sm text-text-muted mb-3">{mode.description}</p>
              <span className="text-xs font-medium text-primary-dark bg-primary/10 px-2 py-1 rounded-full">
                {mode.credits}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/10 rounded-2xl p-8 border border-primary/10 animate-fade-up stagger-5">
        <h2 className="text-2xl font-serif font-medium text-text-main mb-6 flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">tips_and_updates</span>
          Tips for Best Results
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex gap-3">
            <div className="bg-white rounded-lg p-2 h-fit shadow-sm">
              <span className="material-symbols-outlined text-primary">photo_camera</span>
            </div>
            <div>
              <h4 className="font-semibold text-text-main mb-1">Use Clear Product Photos</h4>
              <p className="text-sm text-text-muted">A simple phone photo works. Just make sure your product is in focus and well-lit.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="bg-white rounded-lg p-2 h-fit shadow-sm">
              <span className="material-symbols-outlined text-primary">contrast</span>
            </div>
            <div>
              <h4 className="font-semibold text-text-main mb-1">Even Lighting Helps</h4>
              <p className="text-sm text-text-muted">Products with minimal shadows produce cleaner, more consistent results.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="bg-white rounded-lg p-2 h-fit shadow-sm">
              <span className="material-symbols-outlined text-primary">style</span>
            </div>
            <div>
              <h4 className="font-semibold text-text-main mb-1">Match Style to Product</h4>
              <p className="text-sm text-text-muted">Choose style references that complement your product's aesthetic and target market.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="bg-white rounded-lg p-2 h-fit shadow-sm">
              <span className="material-symbols-outlined text-primary">verified</span>
            </div>
            <div>
              <h4 className="font-semibold text-text-main mb-1">Consistent Results</h4>
              <p className="text-sm text-text-muted">Kalypso delivers consistent, studio-quality results every time — no reshoots needed.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-8 animate-fade-up stagger-6">
        <h3 className="text-xl font-serif font-medium text-text-main mb-2">
          Ready to create listing-ready photos?
        </h3>
        <p className="text-text-muted mb-4">Turn phone photos into premium listing images in seconds.</p>
        <Link to="/" className="btn btn-primary inline-flex">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span>Go to Dashboard</span>
        </Link>
      </div>
    </>
  )
}
